import { useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'

function isUser(
	user: any,
): user is Awaited<ReturnType<typeof rootLoader>>['data']['user'] {
	return user && typeof user === 'object' && typeof user.id === 'string'
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	if (!data || !isUser(data.user)) {
		return undefined
	}
	return data.user
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
		)
	}
	return maybeUser
}

type Action = 'create' | 'read' | 'update' | 'delete'
type Entity = 'user' | 'note' | 'role' | 'permission' | 'content'
type Access = 'own' | 'any' | 'own,any' | 'any,own'
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`

export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(':') as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(',') as Array<Access>) : undefined,
	}
}

export function userHasPermission(
	user:
		| Pick<ReturnType<typeof useUser>, 'roles' | 'effectivePermissions'>
		| null
		| undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)

	// Use effectivePermissions if available (includes role hierarchy + direct permissions)
	const permissions = user.effectivePermissions || []

	// Fallback to role permissions if effectivePermissions not available
	const allPermissions =
		permissions.length > 0
			? permissions
			: user.roles.flatMap((role) => role.permissions)

	return allPermissions.some(
		(p) =>
			p.entity === entity &&
			p.action === action &&
			(!access || access.includes(p.access)),
	)
}

export function userHasRole(
	user: Pick<ReturnType<typeof useUser>, 'roles'> | null,
	role: string,
) {
	if (!user) return false
	return user.roles.some((r) => r.name === role)
}

export function useVisibleTabs(): Set<string> {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	return new Set(data?.visibleTabs ?? [])
}

/** Whether the "Contenus liés" feature is on. Toggled from /admin/parametres. */
export function useRelatedContentEnabled(): boolean {
	const data = useRouteLoaderData<typeof rootLoader>('root')
	return data?.relatedContentEnabled ?? false
}
